import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReferralItem } from "@/components/ReferralItem";

const INIT = { merchant_name: "", contact_email: "", industry: "", origin_country: "", target_jurisdiction: "United States", monthly_volume: "", direction: "finbpm_to_midhq", details: "" };
const TARGETS = ["United States", "Mexico", "Bulgaria", "Amsterdam (NL)", "Other EU", "Other LATAM"];

export function ReferralForm({ onCreated, defaultDirection }) {
  const [f, setF] = useState({ ...INIT, direction: defaultDirection });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/referrals", f);
      onCreated(data);
      setF({ ...INIT, direction: f.direction });
      toast.success("Referral sent to the shared pipeline");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4 border border-white/10 bg-[#0d1524] p-6" data-testid="referral-form">
      <p className="eyebrow">New cross-border referral</p>
      <select className="field" value={f.direction} onChange={set("direction")} data-testid="referral-direction">
        <option value="finbpm_to_midhq">FinBPM → MidHq (US processing)</option>
        <option value="midhq_to_finbpm">MidHq → FinBPM (international MoR)</option>
      </select>
      <input className="field" required placeholder="Merchant name *" value={f.merchant_name} onChange={set("merchant_name")} data-testid="referral-merchant-name" />
      <input className="field" type="email" placeholder="Merchant contact email" value={f.contact_email} onChange={set("contact_email")} data-testid="referral-contact-email" />
      <input className="field" required placeholder="Industry *" value={f.industry} onChange={set("industry")} data-testid="referral-industry" />
      <div className="grid grid-cols-2 gap-3">
        <input className="field" required placeholder="Origin country *" value={f.origin_country} onChange={set("origin_country")} data-testid="referral-origin" />
        <select className="field" value={f.target_jurisdiction} onChange={set("target_jurisdiction")} data-testid="referral-target">
          {TARGETS.map((t) => <option key={t}>{t}</option>)}
        </select>
      </div>
      <input className="field" required placeholder="Projected monthly volume * (e.g. $400k)" value={f.monthly_volume} onChange={set("monthly_volume")} data-testid="referral-volume" />
      <textarea className="field" rows={3} placeholder="Context for the other desk" value={f.details} onChange={set("details")} data-testid="referral-details" />
      <button disabled={busy} className="btn-primary w-full justify-center" data-testid="referral-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Send referral</button>
    </form>
  );
}

export function ReferralFeed({ items, setItems }) {
  const [filter, setFilter] = useState("all");
  const list = filter === "all" ? items : items.filter((r) => r.direction === filter);
  const update = (nr) => setItems(items.map((r) => (r.id === nr.id ? nr : r)));
  return (
    <div data-testid="referral-feed">
      <div className="mb-5 flex flex-wrap gap-2">
        {[["all", "All"], ["finbpm_to_midhq", "Inbound from FinBPM"], ["midhq_to_finbpm", "Outbound to FinBPM"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`referral-filter-${k}`}
            className={`rounded-full border px-4 py-1.5 text-xs ${filter === k ? "border-white bg-white text-black" : "border-white/15 text-slate-300"}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-3">
        {list.length === 0 && <p className="text-sm text-slate-400" data-testid="referral-empty">No referrals yet.</p>}
        {list.map((r) => <ReferralItem key={r.id} r={r} onChange={update} />)}
      </div>
    </div>
  );
}

export default function PartnerPortal() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/referrals").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, []);

  const boarded = items.filter((r) => r.status === "boarded").length;
  return (
    <main className="mx-auto max-w-[1400px] px-5 pb-24 pt-32 sm:px-10" data-testid="partner-portal">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="eyebrow">Corridor Portal · {user?.company}</p>
          <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-6xl">Shared referral desk.</h1>
          <p className="mt-3 text-slate-300">Signed in as {user?.name}. MidHq and FinBPM see the same pipeline.</p>
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <div className="font-mono text-sm"><p className="text-3xl text-white" data-testid="portal-total">{items.length}</p><p className="text-slate-400">referrals</p></div>
          <div className="font-mono text-sm"><p className="text-3xl text-emerald-300" data-testid="portal-boarded">{boarded}</p><p className="text-slate-400">boarded</p></div>
          {user?.role === "admin" && <Link to="/admin" className="btn-ghost !py-2" data-testid="portal-admin-link">Admin</Link>}
          <Link to="/account" className="btn-ghost !py-2" data-testid="portal-account-link">Account</Link>
          <button onClick={logout} className="btn-ghost !py-2" data-testid="portal-logout"><LogOut className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="mt-12 grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-4"><ReferralForm defaultDirection={user?.role === "admin" ? "midhq_to_finbpm" : "finbpm_to_midhq"} onCreated={(r) => setItems([r, ...items])} /></div>
        <div className="lg:col-span-8"><ReferralFeed items={items} setItems={setItems} /></div>
      </div>
    </main>
  );
}
